import * as React from "react";
import {
    AlertDialog,
    AlertDialogContent,
    Button,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Switch,
} from "@jhool-io/fe-components";
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import {
    FlaggedNoteForAuditStatus,
    IChangeCodeResponse,
    NoteStatus,
    NoteTypes,
    Source,
} from "../../types/notes.types";
import { useFetchSessionNote } from "../../../../hooks/queries/note";
import useToast from "../../../../hooks/useToast";
import {
    useChangeCptCode,
    useChangeNoteArchiveStatus,
    useDeleteDraftNote,
    useExportNotes,
    useRecalculateUnitMetadata,
    useRequestSupervisorReview,
    useSkipSupervisorReview,
} from "../../../../hooks/mutations/note";
import { UserRole } from "../../../user-management/types/user-management.types";
import {
    useDoesUserHavePermission,
    useDoesUserHaveRole,
} from "../../../../hooks/permissions";
import { UserPermisions } from "../../../../utils/types/user";
import { useExportPdfs } from "../../../../hooks/queries";
import { IPaginatedApiResponse } from "../../../../utils/types/api-response";
import ApproveNote from "../../../../components/Notes/ApproveNote/ApproveNote";
import RequestChanges from "../../../../components/Notes/RequestChanges/RequestChanges";
import ArchiveNote from "../../../../components/Notes/NoteContent/ArchiveNote/ArchiveNote";
import ChangeCode from "../../../../components/Notes/NoteContent/ChangeCode/ChangeCode";
import { cn } from "../../../../utils/helpers";
import NoteContent from "../NoteContent/NoteContent";
import AddAddendum from "../AddAddendumForm/AddAddendumForm";
import {
    getNoteListNavigateUrl,
    isMedNote,
    isNotePdfExportable,
    isPsychNote,
} from "../../helpers/notes.helpers";
import FlagNoteForm from "../FlagNoteForm/FlagNoteForm";
import ChangeRequestOutcomeForm from "../ChangeRequestOutcomeForm/ChangeRequestOutcomeForm";
import EditManualClaimForm from "../../../claims/components/EditManualClaimForm/EditManualClaimForm";
import { useGetClaim } from "../../../claims/hooks/claims.queries";
import { ClaimStatus } from "../../../claims/types/claims.types";
import { useAppSelector } from "../../../../hooks/useRedux";
import ChevronDownIcon from "../../../../icons/ChevronDown";
import ExportLoaderAndText from "../../../../shared-ui/ExportLoaderAndText/ExportLoaderAndText";
import ChangeCodeFormPsych from "../ChangeCodeFornPsych/ChangeCodeFormPsych";
import { canReviewSessionNoteWithAi } from "../../helpers/aiSessionNotes";
import { AiSessionNoteReviewBoundary } from "../../../../context/AiSessionNoteReviewContext";
import { AiSessionNoteReviewMenuItem } from "../AiSessionNoteReview/AiSessionNoteReview";

type Dialogs =
    | "delete_draft"
    | "note_approval"
    | "request_change"
    | "archive_note"
    | "change_code"
    | "add_addendum"
    | "export_note"
    | "resolve_change"
    | "request_supervisor_review"
    | "skip_supervisor_review"
    | "flag_note"
    | "edit_manual_claim"
    | "unflag_note";

const NOTE_ACTION_FORM_DIALOGS: Dialogs[] = [
    "note_approval",
    "request_change",
    "archive_note",
    "change_code",
    "add_addendum",
    "export_note",
    "flag_note",
    "unflag_note",
];

function SingleNoteActionContent() {
    const [
        requestChangeDialogContentInView,
        setRequestChangeDialogContentInView,
    ] = React.useState<"note_content" | "request_change_form" | "all">(() =>
        window.matchMedia("(max-width: 1000px)").matches
            ? "note_content"
            : "all"
    );
    const [jobId, setJobId] = React.useState<string | null>(null);

    const [searchParams, setSearchParams] = useSearchParams();

    const dialogInView = searchParams.get("modal");

    const { practice } = useAppSelector((state) => state.userPractice);

    // Get id from url params
    const params = useParams();

    const noteId = params.noteId as string;
    const clientId = params.clientId as string;
    const { toast } = useToast();

    const navigate = useNavigate();
    const location = useLocation();

    const { data } = useFetchSessionNote(
        clientId,
        noteId,
        Boolean(clientId) && Boolean(noteId)
    );

    const psychNote = isPsychNote(data?.data?.type);

    const queryClient = useQueryClient();

    const noteListNavigateUrl = getNoteListNavigateUrl(practice?.roles || []);

    const requestChangeSwitchOptions = [
        {
            title: "Note content",
            key: "note_content",
            onClick: () => setRequestChangeDialogContentInView("note_content"),
        },
        {
            title: "Request changes",
            key: "request_change_form",
            onClick: () =>
                setRequestChangeDialogContentInView("request_change_form"),
        },
    ];

    const confirmCptCode = useChangeCptCode();
    const recalculateUnits = useRecalculateUnitMetadata();
    const supervisorReview = useRequestSupervisorReview();
    const skipSupervisorReviewMutation = useSkipSupervisorReview();
    const deleteNote = useDeleteDraftNote();
    const unarchiveNote = useChangeNoteArchiveStatus();
    const exportNotes = useExportNotes();

    // To determine if mutation action is currently running, returns 0 || 1
    const requstChangeIsMutating = useIsMutating(["request-change", noteId]);
    const approveNoteIsMutating = useIsMutating(["approve-note"]);
    const changeCodeIsMutating = useIsMutating(["change-code"]);
    const deleteDraftIsMutating = useIsMutating(["delete-draft-notes"]);
    const addAddendumIsMutating = useIsMutating(["add-addendum"]);
    const archiveNoteIsMutating = useIsMutating(["archive-note"]);
    const flagNoteIsMutating = useIsMutating(["flag-note"]);
    const resolveChangeIsMutating = useIsMutating(["resolve-change"]);
    const editManualClaimIsMutating = useIsMutating(["edit-manual-claim"]);

    const canUserResolveNoteChange = Boolean(
        data?.data.can_resolve_change_request
    );

    // check if logged in user has role to determine action
    const loggedInUserHasRole = useDoesUserHaveRole([
        UserRole.AUDITOR,
        UserRole.SUPER_ADMIN,
        UserRole.CODER,
        UserRole.BILLER_ADMIN,
    ]);

    // Function for open, closing and clearing dialogs
    const handleSetDialogInView = (dialog: Dialogs) => {
        searchParams.set("modal", dialog);
        setSearchParams(searchParams);
    };

    const handleClearModalInView = () => {
        searchParams.delete("modal");
        if (searchParams.get("field_id")) {
            searchParams.delete("field_id");
        }
        navigate(location.pathname, { replace: true });
    };

    const allowedNoteStatusesForAddendum = [
        NoteStatus.PENDING_SUPERVISOR_REVIEW,
        NoteStatus.CHANGES_REQUESTED,
        NoteStatus.PENDING_CODE_REVIEW,
    ];

    const canWriteAddendum =
        data?.data.provider.provider_id === practice?.provider_id ||
        (Boolean(
            practice?.provider_id &&
                practice?.provider_id === data?.data?.provider?.supervisor_id
        ) &&
            allowedNoteStatusesForAddendum.includes(
                data?.data.note_status as NoteStatus
            ));

    const getProviderWithRole = (roleType: string) => {
        const roleAvailable = data?.data.note_signatures?.find(
            (item) => item.role === roleType
        );

        return roleAvailable;
    };

    const canArchiveNote =
        [
            UserPermisions.CLIENT_NOTES_ALL_READ,
            UserPermisions.CLIENT_INFO_NOTES_WRITE,
        ].some((permission) => practice?.permissions?.includes(permission)) ||
        practice?.provider_id === getProviderWithRole("author")?.provider_id ||
        practice?.is_supervisor;

    const canArchiveCodedNote = [
        UserRole.SUPER_ADMIN,
        UserRole.CODER,
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
    ].some((role) => practice?.roles?.includes(role));

    const hasClaim = Boolean(data?.data.claim_id);

    const isBillerOrBillerAdmin = useDoesUserHaveRole([
        UserRole.BILLER,
        UserRole.BILLER_ADMIN,
    ]);

    const canArchiveNoteWithClaim = () => !hasClaim || isBillerOrBillerAdmin;

    const showArchiveOption =
        (canArchiveNote &&
            (data?.data.note_status !== NoteStatus.CODED ||
                !data?.data.date_of_service)) ||
        (canArchiveCodedNote && data?.data.note_status === NoteStatus.CODED);

    const canRequestSupervisorReview =
        (data?.data.note_status === NoteStatus.CODED ||
            data?.data.note_status === NoteStatus.PENDING_CODE_REVIEW) &&
        practice?.permissions?.includes(
            UserPermisions.BILLING_DASHBOARD_READ
        ) &&
        !isMedNote(data?.data.type) &&
        !data?.data.is_archived;

    const canSkipSupervisorReview =
        data?.data.note_status === NoteStatus.PENDING_SUPERVISOR_REVIEW &&
        practice?.permissions?.includes(
            UserPermisions.NOTE_SUPERVISOR_REVIEW_SKIP
        ) &&
        !data?.data.is_archived;

    const canReviewWithAi = canReviewSessionNoteWithAi({
        hasPermission: Boolean(
            practice?.permissions?.includes(
                UserPermisions.CLIENT_INFO_NOTES_AI_REVIEW
            )
        ),
        noteType: data?.data.type,
        hasSavedContent: Boolean(data?.data.note_content),
        isManual: data?.data.source === Source.MANUAL,
        isArchived: Boolean(data?.data.is_archived),
    });

    const showActions = () => {
        if (
            (data?.data.note_status === NoteStatus.PENDING_SUPERVISOR_REVIEW &&
                (practice?.is_supervisor ||
                    practice?.roles?.includes(UserRole.SUPER_ADMIN) ||
                    practice?.roles?.includes(UserRole.BILLER_ADMIN))) ||
            data?.data.provider.provider_id === practice?.provider_id ||
            (data?.data.note_status === NoteStatus.PENDING_CODE_REVIEW &&
                loggedInUserHasRole) ||
            showArchiveOption ||
            practice?.roles?.includes(UserRole.AUDITOR) ||
            canUserResolveNoteChange ||
            canRequestSupervisorReview ||
            canSkipSupervisorReview ||
            canReviewWithAi
        ) {
            return true;
        }
        return false;
    };

    const hasClaimViewPermissions = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_INVOICE_WRITE,
        UserPermisions.CLIENT_INFO_INVOICE_READ,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const canExportNotes = useDoesUserHavePermission([
        UserPermisions.CLIENT_INFO_ALL_WRITE,
        UserPermisions.CLIENT_INFO_NOTES_WRITE,
        UserPermisions.CLIENT_INFO_ALL_ADMIN,
    ]);

    const exportNote = useExportPdfs(
        {
            export_job_id: jobId as string,
            pdf_type: "session_note",
        },
        Boolean(jobId)
    );

    const { data: claimsData } = useGetClaim(
        {
            note_id: data?.data.note_id,
        },
        Boolean(data?.data.note_id) && hasClaimViewPermissions
    );

    if (jobId && !exportNote.isLoading) {
        setJobId(null);
        handleClearModalInView();
    }

    const getDialogHeaderAndSaveText = (currentDialog: Dialogs) => {
        let dialogHeaderAndSaveText: string;
        switch (currentDialog) {
            case "delete_draft":
                dialogHeaderAndSaveText = "Delete Draft";
                break;
            case "note_approval":
                dialogHeaderAndSaveText = "Approve and sign Note";
                break;
            case "request_change":
                dialogHeaderAndSaveText = "Request Change";
                break;
            case "archive_note":
                dialogHeaderAndSaveText = "Archive Note";
                break;
            case "change_code":
                dialogHeaderAndSaveText = "Change code";
                break;
            case "add_addendum":
                dialogHeaderAndSaveText = "Add addendum";
                break;
            case "export_note":
                dialogHeaderAndSaveText = "Export in progress";
                break;
            case "flag_note":
                dialogHeaderAndSaveText = "Flag for Audit";
                break;
            case "unflag_note":
                dialogHeaderAndSaveText = "Unflag Note";
                break;
            default:
                dialogHeaderAndSaveText = "";
        }
        return dialogHeaderAndSaveText;
    };

    const getDialogFormsSubmitValue = (formValue: Dialogs) => {
        let submitValue: string;

        switch (formValue) {
            case "note_approval":
                submitValue = "note-approval";
                break;
            case "request_change":
                submitValue = "request-changes";
                break;
            case "archive_note":
                submitValue = "archive-note";
                break;
            case "change_code":
                submitValue = "change-cpt-code";
                break;
            case "add_addendum":
                submitValue = "add-addendum";
                break;
            case "flag_note":
                submitValue = "flag-note";
                break;
            case "unflag_note":
                submitValue = "flag-note";
                break;
            default:
                return "";
        }

        return submitValue;
    };

    const handleUnarchive = () => {
        const dataToSend = {
            note_ids_to_archive: [noteId],
            archive_status: false,
        };

        unarchiveNote.mutate(dataToSend, {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [`notes`],
                });

                await queryClient.invalidateQueries({
                    queryKey: ["supervisee"],
                });

                await queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });

                toast({
                    mode: "success",
                    message: res.message || "Note unarchived successfully",
                });

                navigate(noteListNavigateUrl);
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not archive note at this time",
                });
            },
        });
    };

    const handleExportNote = () => {
        const dataToSend = {
            session_note_ids: [noteId],
        };

        exportNotes.mutate(dataToSend, {
            onSuccess: async (res) => {
                handleSetDialogInView("export_note");
                await new Promise<void>((resolve) => {
                    const timer = setTimeout(() => {
                        clearTimeout(timer);
                        setJobId(res.data.export_job_id);
                        resolve();
                    }, 8000);
                });
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.message || "Could not export notes at this time",
                });
            },
        });
    };

    const handleConfirmCode = () => {
        const changeCodePayload = {
            [noteId]: {
                cpt_code: data?.data?.cpt_code || "",
                modifier_codes: data?.data?.modifier_codes || [],
                confirm_code: true,
            },
        };
        confirmCptCode.mutate(changeCodePayload, {
            onSuccess: (response) => {
                queryClient.setQueryData<
                    IPaginatedApiResponse<IChangeCodeResponse>
                >([clientId, `session-note`, noteId], (prev) => {
                    const prevRequired =
                        prev as IPaginatedApiResponse<IChangeCodeResponse>;
                    return {
                        ...prevRequired,
                        note_status: response.status,
                    };
                });
                queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });
                queryClient.invalidateQueries({
                    queryKey: ["get-claim", { note_id: noteId }],
                });
                toast({
                    mode: "success",
                    message:
                        response.message || "Cpt code updated successfully",
                });

                if (response?.data?.[0]?.status === NoteStatus.CODED) {
                    navigate(`/billing-and-claims/${clientId}/${noteId}`);
                }
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not change code at this time",
                });
            },
        });
    };

    const handleRecalculateUnits = () => {
        recalculateUnits.mutate(
            { note_ids: [noteId], dry_run: false },
            {
                onSuccess: (response) => {
                    queryClient.invalidateQueries({
                        queryKey: [clientId, `session-note`, noteId],
                    });
                    toast({
                        mode: "success",
                        message:
                            response.message ||
                            "CPT Unit recalculated successfully",
                    });
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not recalculate CPT unit at this time",
                    });
                },
            }
        );
    };

    const handleRequestSupervisorReview = () => {
        supervisorReview.mutate(data?.data.note_id ?? "", {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [`notes`],
                });

                await queryClient.invalidateQueries({
                    queryKey: ["supervisee"],
                });

                await queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });

                toast({
                    mode: "success",
                    message: res.message ?? "Note sent for supervisor review",
                });
                handleClearModalInView();
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ??
                        "Could not request supervisor review at this time",
                });
            },
        });
    };

    const handleSkipSupervisorReview = () => {
        skipSupervisorReviewMutation.mutate([data?.data.note_id ?? ""], {
            onSuccess: async (res) => {
                await queryClient.invalidateQueries({
                    queryKey: [`notes`],
                });

                await queryClient.invalidateQueries({
                    queryKey: ["supervisee"],
                });

                await queryClient.invalidateQueries({
                    queryKey: [clientId, `session-note`, noteId],
                });

                await queryClient.invalidateQueries({
                    queryKey: ["todos"],
                });

                toast({
                    mode: "success",
                    message: res.message ?? "Supervisor review skipped",
                });
                handleClearModalInView();
            },

            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ??
                        "Could not skip supervisor review at this time",
                });
            },
        });
    };

    const handleDeleteNote = () => {
        deleteNote.mutate([noteId], {
            async onSuccess(res) {
                await queryClient.invalidateQueries({
                    queryKey: [`notes`],
                });
                toast({
                    mode: "success",
                    message: res.message || "Draft note deleted successfully",
                });
                handleClearModalInView(); // Redirect to notes list
                navigate(`/notes`);
            },
            onError(err) {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Cannot delete note at this moment, please try again",
                });
            },
        });
    };

    const canRequestChange =
        (useDoesUserHaveRole([
            UserRole.AUDITOR,
            UserRole.BILLER,
            UserRole.SUPER_ADMIN,
            UserRole.AUDITOR_ADMIN,
        ]) ||
            practice?.is_supervisor) &&
        practice?.provider_id !== getProviderWithRole("author")?.provider_id;

    const handleAiReview = () => {
        searchParams.set("tab", "note");
        searchParams.set("note_tab", "content");
        searchParams.delete("modal");
        setSearchParams(searchParams);
    };

    // Function to determine what content to render inside the actions dropdown
    const handleDropdownOptionsToShow = (
        status: NoteStatus,
        noteType: NoteTypes,
        roles: string[]
    ) => {
        const reviewNoteWithAiDropdownItem = canReviewWithAi ? (
            <AiSessionNoteReviewMenuItem onReviewStart={handleAiReview} />
        ) : null;
        switch (status) {
            case NoteStatus.DRAFT:
                return (
                    <>
                        <DropdownItem
                            onClick={() =>
                                navigate(
                                    `/notes/${data?.data.note_id}/${data?.data.client.client_id}/edit?note_type=${data?.data.type}&session_history_id=${data?.data.session_history_id}&appt_type=${data?.data.appointment_type}`
                                )
                            }
                        >
                            Edit note
                        </DropdownItem>
                        <DropdownItem
                            onClick={() =>
                                handleSetDialogInView("delete_draft")
                            }
                        >
                            Delete draft
                        </DropdownItem>
                        {reviewNoteWithAiDropdownItem}
                    </>
                );
            case NoteStatus.PENDING_SUPERVISOR_REVIEW:
                return (
                    <>
                        {(practice?.is_supervisor ||
                            roles.includes(UserRole.SUPER_ADMIN) ||
                            (roles.includes(UserRole.AUDITOR) &&
                                practice?.provider_id)) &&
                        !data?.data.is_archived ? (
                            <DropdownItem
                                onClick={() =>
                                    handleSetDialogInView("note_approval")
                                }
                            >
                                Approve and sign note
                            </DropdownItem>
                        ) : null}
                        {canRequestChange &&
                            practice?.permissions?.includes(
                                UserPermisions.CLIENT_INFO_NOTES_CHANGE_REQUEST_WRITE
                            ) &&
                            !data?.data.is_archived && (
                                <DropdownItem
                                    onClick={() =>
                                        handleSetDialogInView("request_change")
                                    }
                                >
                                    Request changes
                                </DropdownItem>
                            )}
                        {canWriteAddendum && (
                            <DropdownItem
                                onClick={() =>
                                    handleSetDialogInView("add_addendum")
                                }
                            >
                                Add addendum
                            </DropdownItem>
                        )}
                        {practice?.permissions?.includes(
                            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
                        ) &&
                            !roles.includes(UserRole.AUDITOR_ADMIN) &&
                            data?.data.flagged_for_audit_status &&
                            data?.data.flagged_for_audit_status !==
                                FlaggedNoteForAuditStatus.UNFLAGGED && (
                                <DropdownItem
                                    onClick={() =>
                                        handleSetDialogInView("unflag_note")
                                    }
                                >
                                    Unflag note
                                </DropdownItem>
                            )}
                        {canSkipSupervisorReview && (
                            <DropdownItem
                                onClick={() =>
                                    handleSetDialogInView(
                                        "skip_supervisor_review"
                                    )
                                }
                            >
                                Skip supervisor review
                            </DropdownItem>
                        )}
                        {reviewNoteWithAiDropdownItem}
                    </>
                );

            case NoteStatus.PENDING_CODE_REVIEW:
                return (
                    <div>
                        {(!data?.data.flagged_for_audit_status ||
                            data?.data.flagged_for_audit_status ===
                                FlaggedNoteForAuditStatus.UNFLAGGED) &&
                        (roles.includes("coder") ||
                            roles.includes("superadmin") ||
                            roles.includes("billeradmin")) ? (
                            <div>
                                {noteType === NoteTypes.CANCELLATION_NOTE ||
                                noteType === NoteTypes.SAFETY_PLAN ||
                                (noteType === NoteTypes.SUPERVISION_NOTE &&
                                    !psychNote) ? (
                                    <DropdownItem onClick={handleConfirmCode}>
                                        Confirm code
                                    </DropdownItem>
                                ) : (
                                    <>
                                        <DropdownItem
                                            onClick={handleConfirmCode}
                                        >
                                            Confirm code
                                        </DropdownItem>
                                        <DropdownItem
                                            onClick={() =>
                                                handleSetDialogInView(
                                                    "change_code"
                                                )
                                            }
                                        >
                                            Change code
                                        </DropdownItem>
                                        {psychNote && (
                                            <DropdownItem
                                                onClick={handleRecalculateUnits}
                                            >
                                                Recalculate CPT codes
                                            </DropdownItem>
                                        )}
                                    </>
                                )}
                            </div>
                        ) : null}
                        {canRequestChange &&
                            practice?.permissions?.includes(
                                UserPermisions.CLIENT_INFO_NOTES_CHANGE_REQUEST_WRITE
                            ) &&
                            data?.data.flagged_for_audit_status !==
                                FlaggedNoteForAuditStatus.SENT_FOR_CHANGES && (
                                <DropdownItem
                                    onClick={() =>
                                        handleSetDialogInView("request_change")
                                    }
                                >
                                    Request changes
                                </DropdownItem>
                            )}
                        {canWriteAddendum &&
                        (!data?.data.flagged_for_audit_status ||
                            data?.data.flagged_for_audit_status ===
                                FlaggedNoteForAuditStatus.UNFLAGGED) ? (
                            <DropdownItem
                                onClick={() =>
                                    handleSetDialogInView("add_addendum")
                                }
                            >
                                Add addendum
                            </DropdownItem>
                        ) : null}
                        {practice?.permissions?.includes(
                            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
                        ) &&
                            !roles.includes(UserRole.AUDITOR_ADMIN) &&
                            (data?.data.flagged_for_audit_status ===
                                FlaggedNoteForAuditStatus.FLAGGED ||
                            data?.data.flagged_for_audit_status ===
                                FlaggedNoteForAuditStatus.SENT_FOR_CHANGES ||
                            data?.data.flagged_for_audit_status ===
                                FlaggedNoteForAuditStatus.REQUIRES_ACTION ? (
                                <DropdownItem
                                    onClick={() =>
                                        handleSetDialogInView("unflag_note")
                                    }
                                >
                                    Unflag note
                                </DropdownItem>
                            ) : (
                                <DropdownItem
                                    onClick={() =>
                                        handleSetDialogInView("flag_note")
                                    }
                                >
                                    Flag for audit
                                </DropdownItem>
                            ))}
                        {canRequestSupervisorReview && (
                            <DropdownItem
                                onClick={() =>
                                    handleSetDialogInView(
                                        "request_supervisor_review"
                                    )
                                }
                            >
                                Request supervisor review
                            </DropdownItem>
                        )}
                        {reviewNoteWithAiDropdownItem}
                    </div>
                );
            case NoteStatus.CODED:
                return (
                    <div>
                        {data?.data.note_content &&
                            canRequestChange &&
                            data?.data.flagged_for_audit_status !==
                                FlaggedNoteForAuditStatus.SENT_FOR_CHANGES && (
                                <DropdownItem
                                    onClick={() =>
                                        handleSetDialogInView("request_change")
                                    }
                                >
                                    Request changes
                                </DropdownItem>
                            )}
                        {practice?.permissions?.includes(
                            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
                        ) &&
                            !roles.includes(UserRole.AUDITOR_ADMIN) && (
                                <>
                                    {data?.data.flagged_for_audit_status &&
                                    data?.data.flagged_for_audit_status !==
                                        FlaggedNoteForAuditStatus.UNFLAGGED ? (
                                        <DropdownItem
                                            onClick={() =>
                                                handleSetDialogInView(
                                                    "unflag_note"
                                                )
                                            }
                                        >
                                            Unflag note
                                        </DropdownItem>
                                    ) : null}
                                    {data?.data.source !== Source.MANUAL &&
                                    (data?.data.flagged_for_audit_status ===
                                        FlaggedNoteForAuditStatus.UNFLAGGED ||
                                        !data?.data
                                            .flagged_for_audit_status) ? (
                                        <DropdownItem
                                            onClick={() =>
                                                handleSetDialogInView(
                                                    "flag_note"
                                                )
                                            }
                                        >
                                            Flag for audit
                                        </DropdownItem>
                                    ) : null}
                                </>
                            )}
                        {claimsData?.data &&
                            (data?.data.source === Source.MANUAL &&
                            claimsData?.data[0]?.claim_status ===
                                ClaimStatus.READY_FOR_SUBMISSION &&
                            (data?.data.flagged_for_audit_status ===
                                FlaggedNoteForAuditStatus.UNFLAGGED ||
                                !data?.data.flagged_for_audit_status) ? (
                                <DropdownItem
                                    onClick={() =>
                                        handleSetDialogInView(
                                            "edit_manual_claim"
                                        )
                                    }
                                >
                                    Edit note details
                                </DropdownItem>
                            ) : null)}
                        {canRequestSupervisorReview && (
                            <DropdownItem
                                onClick={() =>
                                    handleSetDialogInView(
                                        "request_supervisor_review"
                                    )
                                }
                            >
                                Request supervisor review
                            </DropdownItem>
                        )}
                        {reviewNoteWithAiDropdownItem}
                    </div>
                );
            case NoteStatus.CHANGES_REQUESTED:
                return (
                    <div>
                        {roles.includes(UserRole.AUDITOR) ||
                            (data?.data.flagged_for_audit_status !==
                                FlaggedNoteForAuditStatus.SENT_FOR_CHANGES && (
                                <DropdownItem
                                    onClick={() =>
                                        handleSetDialogInView("request_change")
                                    }
                                >
                                    Request changes
                                </DropdownItem>
                            ))}
                        {practice?.permissions?.includes(
                            UserPermisions.CLIENT_INFO_NOTES_FLAG_FOR_AUDIT_WRITE
                        ) &&
                            !roles.includes(UserRole.AUDITOR_ADMIN) &&
                            (data?.data.flagged_for_audit_status !==
                                FlaggedNoteForAuditStatus.UNFLAGGED ||
                            !data?.data.flagged_for_audit_status ? (
                                <DropdownItem
                                    onClick={() =>
                                        handleSetDialogInView("unflag_note")
                                    }
                                >
                                    Unflag note
                                </DropdownItem>
                            ) : null)}
                        {canWriteAddendum &&
                        (!data?.data.flagged_for_audit_status ||
                            data?.data.flagged_for_audit_status ===
                                FlaggedNoteForAuditStatus.UNFLAGGED ||
                            data?.data.flagged_for_audit_status ===
                                FlaggedNoteForAuditStatus.SENT_FOR_CHANGES) ? (
                            <DropdownItem
                                onClick={() =>
                                    handleSetDialogInView("add_addendum")
                                }
                            >
                                Add addendum
                            </DropdownItem>
                        ) : null}
                        {reviewNoteWithAiDropdownItem}
                    </div>
                );
            default:
                return null;
        }
    };

    const handleDialogContentToDisplay = () => {
        switch (dialogInView) {
            case "note_approval":
                return (
                    <ApproveNote
                        noteId={noteId}
                        onFormSubmit={handleClearModalInView}
                    />
                );
            case "request_change":
                return <RequestChanges onFormSubmit={handleClearModalInView} />;
            case "archive_note":
                return (
                    <ArchiveNote
                        onFormSubmit={handleClearModalInView}
                        noteId={noteId}
                    />
                );
            case "change_code":
                return !psychNote ? (
                    <ChangeCode onFormSubmit={handleClearModalInView} />
                ) : (
                    <ChangeCodeFormPsych
                        onFormSubmit={handleClearModalInView}
                    />
                );
            case "add_addendum":
                return <AddAddendum onFormSubmit={handleClearModalInView} />;
            case "export_note":
                return <ExportLoaderAndText />;
            case "flag_note":
                return (
                    <FlagNoteForm
                        onFormSubmit={handleClearModalInView}
                        noteId={noteId}
                        action="flag"
                    />
                );
            case "unflag_note":
                return (
                    <FlagNoteForm
                        onFormSubmit={handleClearModalInView}
                        noteId={noteId}
                        action="unflag"
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <Dialog open={dialogInView === "request_supervisor_review"}>
                <DialogContent
                    title="Request Supervisor Review"
                    handleCloseDialog={handleClearModalInView}
                    showFooter
                    saveText="Request review"
                    variant="center"
                    onSaveClick={handleRequestSupervisorReview}
                    cancelText="Cancel"
                    isSubmitBtnDisabled={supervisorReview.isLoading}
                    isCancelBtnDisabled={supervisorReview.isLoading}
                >
                    <div>
                        <h3 className="font-medium text-xl mb-8">
                            You are about to request a supervisor review for
                            this note
                        </h3>
                        <p className="text-gray font-normal text-base">
                            Do you wish to continue?
                        </p>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={dialogInView === "skip_supervisor_review"}>
                <DialogContent
                    title="Skip Supervisor Review"
                    handleCloseDialog={handleClearModalInView}
                    showFooter
                    saveText="Skip review"
                    variant="center"
                    onSaveClick={handleSkipSupervisorReview}
                    cancelText="Cancel"
                    isSubmitBtnDisabled={skipSupervisorReviewMutation.isLoading}
                    isCancelBtnDisabled={skipSupervisorReviewMutation.isLoading}
                >
                    <div>
                        <h3 className="font-medium text-xl mb-8">
                            You are about to skip supervisor review and send
                            this note to code review
                        </h3>
                        <p className="text-gray font-normal text-base">
                            Do you wish to continue?
                        </p>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={dialogInView === "resolve_change"}>
                <DialogContent
                    title="Resolve Change"
                    handleCloseDialog={handleClearModalInView}
                    showFooter
                    saveText="Resolve change"
                    variant="center"
                    submitBtnFormValue="change-request-outcome"
                    cancelText="Cancel"
                    isSubmitBtnDisabled={resolveChangeIsMutating > 0}
                    isCancelBtnDisabled={resolveChangeIsMutating > 0}
                >
                    <ChangeRequestOutcomeForm
                        noteId={noteId}
                        onFormSubmit={handleClearModalInView}
                    />
                </DialogContent>
            </Dialog>

            <AlertDialog open={dialogInView === "delete_draft"}>
                <AlertDialogContent
                    title="Delete draft note"
                    description="Are you sure you want to delete this draft note? This cannot be undone."
                    isSaveBtnDisabled={deleteNote.isLoading}
                    isCancelBtnDisabled={deleteNote.isLoading}
                    onCancelClick={handleClearModalInView}
                    onSaveClick={
                        dialogInView === "delete_draft"
                            ? handleDeleteNote
                            : undefined
                    }
                    saveText="Delete Draft"
                    isDeleting
                />
            </AlertDialog>
            <Dialog
                open={NOTE_ACTION_FORM_DIALOGS.includes(
                    dialogInView as Dialogs
                )}
            >
                <DialogContent
                    title={getDialogHeaderAndSaveText(dialogInView as Dialogs)}
                    handleCloseDialog={handleClearModalInView}
                    showFooter={dialogInView !== "export_note"}
                    saveText={getDialogHeaderAndSaveText(
                        dialogInView as Dialogs
                    )}
                    variant={
                        dialogInView !== "request_change" ? "center" : "drawer"
                    }
                    cancelText="Cancel"
                    submitBtnFormValue={getDialogFormsSubmitValue(
                        dialogInView as Dialogs
                    )}
                    isSubmitBtnDisabled={
                        requstChangeIsMutating > 0 ||
                        approveNoteIsMutating > 0 ||
                        deleteDraftIsMutating > 0 ||
                        changeCodeIsMutating > 0 ||
                        addAddendumIsMutating > 0 ||
                        archiveNoteIsMutating > 0 ||
                        flagNoteIsMutating > 0
                    }
                    isCancelBtnDisabled={
                        requstChangeIsMutating > 0 ||
                        approveNoteIsMutating > 0 ||
                        deleteDraftIsMutating > 0 ||
                        changeCodeIsMutating > 0 ||
                        addAddendumIsMutating > 0 ||
                        archiveNoteIsMutating > 0 ||
                        flagNoteIsMutating > 0
                    }
                    wrapperClass={
                        dialogInView === "request_change"
                            ? "w-[3000px]"
                            : undefined
                    }
                >
                    {dialogInView === "request_change" ? (
                        <div className="relative">
                            {requestChangeDialogContentInView !== "all" && (
                                <div className="fixed top-[52px] left-[72px]">
                                    <Switch
                                        switchOptions={
                                            requestChangeSwitchOptions
                                        }
                                        activeSwitch={
                                            requestChangeDialogContentInView
                                        }
                                    />
                                </div>
                            )}

                            <div
                                className={cn(
                                    "block min-[1000px]:flex min-[1000px]:justify-between",
                                    {
                                        "mt-[48px]":
                                            requestChangeDialogContentInView !==
                                            "all",
                                    }
                                )}
                            >
                                {requestChangeDialogContentInView === "all" ||
                                requestChangeDialogContentInView ===
                                    "note_content" ? (
                                    <div className="h-auto basis-1/2 min-[1000px]:h-[75vh] overflow-auto pr-20">
                                        <NoteContent
                                            showSideNavigation={false}
                                        />
                                    </div>
                                ) : null}
                                <div className="basis-[45%]">
                                    {requestChangeDialogContentInView ===
                                        "all" ||
                                    requestChangeDialogContentInView ===
                                        "request_change_form"
                                        ? handleDialogContentToDisplay()
                                        : null}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <>{handleDialogContentToDisplay()}</>
                    )}
                </DialogContent>
            </Dialog>
            <Dialog open={dialogInView === "edit_manual_claim"}>
                <DialogContent
                    title="Edit manual claim"
                    variant="center"
                    handleCloseDialog={handleClearModalInView}
                    showFooter
                    cancelText="Cancel"
                    saveText="Edit claim"
                    submitBtnFormValue="edit-manual-claim"
                    isSubmitBtnDisabled={editManualClaimIsMutating > 0}
                    isCancelBtnDisabled={editManualClaimIsMutating > 0}
                >
                    <EditManualClaimForm
                        onFormSubmit={handleClearModalInView}
                    />
                </DialogContent>
            </Dialog>
            {showActions() && data ? (
                <Dropdown>
                    <DropdownTrigger asChild>
                        <Button
                            aria-label="note-action-button"
                            variant="primary"
                            type="button"
                            iconRight={<ChevronDownIcon />}
                        >
                            Actions
                        </Button>
                    </DropdownTrigger>
                    <DropdownContent width="auto" align="end">
                        {!data?.data.is_archived
                            ? handleDropdownOptionsToShow(
                                  data?.data.note_status,
                                  data?.data.type,
                                  practice?.roles || []
                              )
                            : null}

                        {showArchiveOption &&
                            canArchiveNoteWithClaim() &&
                            !data?.data.is_archived &&
                            data?.data.note_status !== NoteStatus.DRAFT && (
                                <DropdownItem
                                    onClick={() =>
                                        handleSetDialogInView("archive_note")
                                    }
                                >
                                    Archive note
                                </DropdownItem>
                            )}

                        {showArchiveOption && data.data.is_archived && (
                            <DropdownItem onClick={() => handleUnarchive()}>
                                Unarchive note
                            </DropdownItem>
                        )}

                        {canExportNotes &&
                            isNotePdfExportable({
                                note_type: data.data.type,
                                status: data.data.note_status,
                                note_url: data.data.note_url,
                                source: data.data.source,
                            }) &&
                            (data.data.source === Source.MANUAL ||
                                data.data.note_content) && (
                                <DropdownItem onClick={handleExportNote}>
                                    Export to pdf
                                </DropdownItem>
                            )}
                        {canUserResolveNoteChange ? (
                            <DropdownItem
                                onClick={() =>
                                    handleSetDialogInView("resolve_change")
                                }
                            >
                                Resolve change
                            </DropdownItem>
                        ) : null}
                    </DropdownContent>
                </Dropdown>
            ) : null}
        </>
    );
}

export default function SingleNoteActions() {
    const { noteId } = useParams();
    return (
        <AiSessionNoteReviewBoundary noteId={noteId as string}>
            <SingleNoteActionContent />
        </AiSessionNoteReviewBoundary>
    );
}
